import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { waHref } from "@/components/Nav";

export default function Services() {
  const { lang, t } = useLang();
  const { content } = useContent();
  const s = content && content.services;
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(0);
  if (!s) return null;
  const items = s.items;
  const contacts = (content && content.contacts) || { whatsapp: "393751163111" };

  return (
    <section id="servizi" className="relative py-24 sm:py-32 lg:py-40" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>{t(s.kicker)}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display mt-6 text-3xl font-light leading-tight text-[#FAF9F6] sm:text-4xl lg:text-5xl" data-testid="services-title">
              {t(s.title)}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 text-sm font-light leading-relaxed text-[#A1A1AA] sm:text-base">{t(s.intro)}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-12 lg:mt-20 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {items.map((item, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={item.id} delay={i * 0.05}>
                  <article className={`border-t hairline ${i === items.length - 1 ? "border-b" : ""}`} data-testid={`service-item-${item.id}`}>
                    <button
                      className="flex w-full items-baseline gap-6 py-7 text-left sm:gap-10"
                      onClick={() => {
                        setOpen(isOpen ? -1 : i);
                        setActive(i);
                      }}
                      onMouseEnter={() => setActive(i)}
                      data-testid={`service-toggle-${item.id}`}
                      aria-expanded={isOpen}
                    >
                      <span className="font-display text-sm text-[#C5A059]">{item.num}</span>
                      <span className="flex-1">
                        <span className="font-display block text-2xl font-light text-[#FAF9F6] transition-colors duration-300 sm:text-3xl">
                          {item.title[lang] || item.title.it}
                        </span>
                        <span className="mt-1 block text-[17px] font-light text-[#A1A1AA]">
                          {item.tagline[lang] || item.tagline.it}
                        </span>
                      </span>
                      <span
                        className={`relative h-4 w-4 shrink-0 self-center transition-transform duration-500 ${isOpen ? "rotate-45" : ""}`}
                        aria-hidden="true"
                      >
                        <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-[#C5A059]" />
                        <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#C5A059]" />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 gap-6 pb-9 sm:grid-cols-2 sm:items-start">
                            <div>
                              <p className="text-[13px] font-light leading-relaxed text-[#A1A1AA] sm:text-sm">
                                {item.desc[lang] || item.desc.it}
                              </p>
                              <p className="mt-4 text-[13px] font-light leading-relaxed text-[#C5A059]/90">
                                {item.detail[lang] || item.detail.it}
                              </p>
                              <a
                                href={waHref(
                                  { whatsapp: contacts.whatsapp, whatsapp_msg: { it: `Ciao, vorrei richiedere: ${item.title.it}.`, en: `Hello, I would like to request: ${item.title.en}.` } },
                                  lang
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FAF9F6] underline-offset-4 transition-colors hover:text-[#C5A059] hover:underline"
                                data-testid={`service-cta-${item.id}`}
                              >
                                {item.cta[lang] || item.cta.it}
                              </a>
                            </div>
                            <div className="relative overflow-hidden lg:hidden">
                              <img
                                src={item.image}
                                alt={item.title[lang] || item.title.it}
                                className="img-spotlight aspect-[4/3] w-full object-cover"
                                loading="lazy"
                              />
                              <div className="pointer-events-none absolute inset-0 border hairline" />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </article>
                </Reveal>
              );
            })}
            <Reveal delay={0.1}>
              <p className="mt-6 text-[15px] font-light leading-relaxed text-[#FAF9F6]/45">{t(s.microcopy)}</p>
            </Reveal>
          </div>

          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-28">
              <Reveal delay={0.15}>
                <div className="relative aspect-[4/5] overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={items[active].id + items[active].image}
                      src={items[active].image}
                      alt={items[active].title[lang] || items[active].title.it}
                      initial={{ opacity: 0, scale: 1.03 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      className="img-spotlight absolute inset-0 h-full w-full object-cover"
                      data-testid="services-preview-image"
                    />
                  </AnimatePresence>
                  <div className="pointer-events-none absolute inset-0 border hairline" />
                  <div className="absolute bottom-5 left-5 flex items-center gap-3">
                    <span className="font-display text-3xl font-light italic text-[#C5A059]">{items[active].num}</span>
                    <span className="eyebrow !text-[9px] text-[#FAF9F6]/80">{items[active].title[lang] || items[active].title.it}</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
